import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import InteractiveSlider from './InteractiveSlider';
import DisclaimerModal from './DisclaimerModal';
import GuidanceModal from './GuidanceModal';
import GoogleAdInterstitial from './GoogleAdInterstitial';
import CollapsibleSection from './CollapsibleSection';
import ProgressIndicator from './ProgressIndicator';
import SummaryCard from './SummaryCard';
import CO2SummaryCard from './CO2SummaryCard';
import CostBreakdownTable from './carResults/CostBreakdownTable';
import StructuredData from './StructuredData';
import { useTracker } from '../hooks/useTracker';
import { useCountry } from '../contexts/CountryContext';
import { getAverageUSSalesTax, CA_PROVINCE_TAX_RATES } from '../utils/usStateTaxRates';
import { User } from '../types/auth';
import { CarComparisonData, SavedCarScenario, LeaseInputMode, BreakdownScenario, BreakdownPeriod, AnalysisSection } from '../types/carComparison';
import { calculateResults, calculateComparisonResults, calculateLeaseResidualValue, calculateLeaseAPR, calculateLeasePayment, calculateMonthlyPayment } from '../utils/carCalculations';
import GeneralInputs from './carInputs/GeneralInputs';
import BuyInputs from './carInputs/BuyInputs';
import FinanceInputs from './carInputs/FinanceInputs';
import LeaseInputs from './carInputs/LeaseInputs';
import GeneralCosts from './carInputs/GeneralCosts';
import ComparisonResults from './carResults/ComparisonResults';
import SingleCarResults from './carResults/SingleCarResults';
import DetailedAnalysis from './carResults/DetailedAnalysis';
import CarPDFService from '../services/car-pdf-service';
import carScenariosService from '../services/car-scenarios';
import scenarioService from '../services/scenario-service';
import { TylerState, useTylerStateUpdate, updateReact } from '../utils/TylerState';
import { debugLog } from '../utils/debugLogger';
import './CollapsibleSection.css';
import './SummaryCard.css';

interface CarComparisonCalculatorProps {
  user: User | null;
  guestScenario?: any;
  onSaveScenario?: (data: any) => void;
}

const CarComparisonCalculator: React.FC<CarComparisonCalculatorProps> = ({ user, guestScenario, onSaveScenario }) => {
  // Subscribe to TylerState updates for reactive user changes
  useTylerStateUpdate();

  // Get country from global context
  const { isUSA, countryName } = useCountry();

  // Set page title and meta description for SEO
  useEffect(() => {
    const region = isUSA ? 'USA' : 'Canada';
    document.title = `Car Lease vs Buy Calculator ${region} | Larry's Help`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `Compare car lease vs buy costs in ${region}. Calculate total costs including depreciation, interest rates, maintenance, and tax benefits. Make informed car financing decisions.`);
    }
  }, [isUSA]);

  const [showResults, setShowResults] = useState(false);
  const [showBuyBreakdown, setShowBuyBreakdown] = useState(false);
  const [showFinanceBreakdown, setShowFinanceBreakdown] = useState(false);
  const [showLeaseBreakdown, setShowLeaseBreakdown] = useState(false);
  const [selectedBreakdownScenario, setSelectedBreakdownScenario] = useState<BreakdownScenario>('buy');
  const [selectedBreakdownPeriod, setSelectedBreakdownPeriod] = useState<BreakdownPeriod>('total');
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [useCalculatedLeasePayment, setUseCalculatedLeasePayment] = useState(false);
  const [leaseInputMode, setLeaseInputMode] = useState<LeaseInputMode>('payment-apr');
  const [includeLeaseScenario, setIncludeLeaseScenario] = useState(false);
  const [includeFinanceScenario, setIncludeFinanceScenario] = useState(false);
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [guidanceContent, setGuidanceContent] = useState('');
  const [guidanceTitle, setGuidanceTitle] = useState('');
  const guidanceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [inputsChanged, setInputsChanged] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);

  // Saved scenarios state
  const [savedScenarios, setSavedScenarios] = useState<SavedCarScenario[]>([]);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSavedList, setShowSavedList] = useState(true);

  // Scenario names for Car A and Car B
  const [scenarioAName, setScenarioAName] = useState("Car A");
  const [scenarioBName, setScenarioBName] = useState("Car B");
  // Analytics tracking
  const { trackButton, trackInput, trackError, trackCalculatorUsage } = useTracker();

  const leaseCalculationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load saved scenarios from backend
  const loadSavedScenarios = async () => {
    const currentUser = TylerState.user;
    if (!currentUser?.email) {
      setSavedScenarios([]);
      return;
    }

    // ONE-TIME CLEANUP: Remove old localStorage car scenarios
    try {
      const oldKeys = Object.keys(localStorage).filter(key =>
        key.includes('car_scenarios_') ||
        key === 'larryshelp_car_scenarios' ||
        key === 'USE_REFACTORED_CAR_CALCULATOR'
      );
      oldKeys.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (cleanupError) {
      console.warn('localStorage cleanup failed (non-critical):', cleanupError);
    }

    try {
      const scenariosArray = await carScenariosService.getCarScenariosArray();
      setSavedScenarios(scenariosArray);
    } catch (error) {
      console.error('Error loading scenarios:', error);
      setSavedScenarios([]);
    }
  };

  useEffect(() => {
    loadSavedScenarios();
  }, [TylerState.user?.email]);

  // Auto-save function - debounced save after input changes
  // Removed debounced autosave - Tyler wants save only on: calculate, manual save, screen change

  // Listen for save modal trigger from CarSavedScenarios
  useEffect(() => {
    const handleTriggerSaveModal = (event: CustomEvent) => {
      setSaveName(event.detail.name);
      setShowSaveModal(true);
    };

    window.addEventListener("trigger-save-modal", handleTriggerSaveModal as EventListener);
    return () => {
      window.removeEventListener("trigger-save-modal", handleTriggerSaveModal as EventListener);
    };
  }, []);

  const getScenarioCap = (tier: User['tier']) => {
    if (tier === 'supporter') return 1000;
    return 1; // free default
  };

  const handleSaveLoggedIn = () => {
    const currentUser = TylerState.user;
    if (!currentUser) {
      window.dispatchEvent(new Event('open-login-modal'));
      return;
    }
    // Show the save modal
    setShowSaveModal(true);
  };

  const handleSaveModalSubmit = async () => {
    const currentUser = TylerState.user;
    debugLog('SAVE TRIGGERED', { userEmail: currentUser?.email });

    if (!currentUser) return;

    const cap = getScenarioCap(currentUser?.tier || 'free');
    const name = (saveName || data.scenarioName || '').trim();
    const finalName = name || `Car Scenario ${new Date().toLocaleString()}`;
    const existingScenario = savedScenarios.find(s => s.name.toLowerCase() === finalName.toLowerCase());

    // Check tier limits for new scenarios
    if (!existingScenario && savedScenarios.length >= cap) {
      setGuidanceTitle('Save Limit Reached');
      setGuidanceContent(`Free accounts can save ${cap} scenario${cap === 1 ? '' : 's'}.<br><br>Upgrade to Supporter ($10/year) for unlimited saves and advanced features.`);
      setShowGuidanceModal(true);
      setShowSaveModal(false);
      return;
    }

    const updatedData = {
      ...data,
      scenarioName: finalName,
      includeFinanceScenario,
      includeLeaseScenario
    };

    debugLog('💾 SAVING SCENARIO', {
      name: finalName,
      isUpdate: !!existingScenario,
      region: updatedData.region,
      totalCost: updatedData.totalCost,
      annualInsurance: updatedData.annualInsurance
    });

    try {
      if (existingScenario) {
        await carScenariosService.updateCarScenario(existingScenario.id, {
          name: finalName,
          description: saveDescription.trim() || undefined,
          data: updatedData
        });
        debugLog('✅ SAVE SUCCESS (UPDATE)', { name: finalName, id: existingScenario.id });
      } else {
        const newId = await carScenariosService.saveCarScenario({
          name: finalName,
          description: saveDescription.trim() || undefined,
          data: updatedData
        });
        debugLog('✅ SAVE SUCCESS (NEW)', { name: finalName, id: newId });
      }

      await loadSavedScenarios();
      setData(updatedData);
      setSaveName('');
      setSaveDescription('');
      setShowSaveModal(false);
      setGuidanceTitle(existingScenario ? 'Updated' : 'Saved');
      setGuidanceContent(`Your car scenario was ${existingScenario ? 'updated' : 'saved'}. You can load it ${currentUser?.tier === 'supporter' ? 'into Car A or Car B' : 'anytime'}.`);
      setShowGuidanceModal(true);
    } catch (error) {
      console.error('Error saving scenario:', error);
      setGuidanceTitle('Save Failed');
      setGuidanceContent('Failed to save scenario. Please try again.');
      setShowGuidanceModal(true);
    }
  };

  const handleSaveGuest = () => {
    if (onSaveScenario) {
      onSaveScenario({ data, createdAt: Date.now() });
      setGuidanceTitle('Saved for 24 Hours');
      setGuidanceContent('Your scenario is saved temporarily. Create a free account to keep unlimited scenarios.');
      setShowGuidanceModal(true);
    } else {
      window.dispatchEvent(new Event('open-login-modal'));
    }
  };

  const handleLoadScenarioToA = (scenarioData: CarComparisonData, scenarioName?: string, timestamp?: number) => {
    const finalScenarioName = scenarioName || scenarioData.scenarioName || "Car A";

    debugLog('📂 LOADING CAR DATA (Car A)', {
      name: finalScenarioName,
      region: scenarioData.region,
      totalCost: scenarioData.totalCost,
      annualInsurance: scenarioData.annualInsurance
    });

    // Merge with current data to preserve any new fields with defaults
    setData(prev => ({ ...prev, ...scenarioData, scenarioName: finalScenarioName }));
    setSaveName(finalScenarioName);
    // Keep TylerState in sync for backward compatibility
    TylerState.carNameA = finalScenarioName;

    // Restore checkbox states
    if (scenarioData.includeFinanceScenario !== undefined) {
      setIncludeFinanceScenario(scenarioData.includeFinanceScenario);
      TylerState.car.includeFinanceScenario = scenarioData.includeFinanceScenario;
    }
    if (scenarioData.includeLeaseScenario !== undefined) {
      setIncludeLeaseScenario(scenarioData.includeLeaseScenario);
      TylerState.car.includeLeaseScenario = scenarioData.includeLeaseScenario;
    }
    updateReact();

    setInputsChanged(true);
    setShowResults(false);

    // Show success modal with timestamp
    const formattedDate = timestamp
      ? new Date(timestamp).toLocaleString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      : 'Unknown';

    setGuidanceTitle('Scenario Loaded');
    setGuidanceContent(`<strong>${finalScenarioName}</strong> loaded successfully.<br><br>Last Updated: ${formattedDate}`);
    setShowGuidanceModal(true);
  };

  const handleLoadScenarioToB = (scenarioData: CarComparisonData, scenarioName?: string, timestamp?: number) => {
    const finalScenarioName = scenarioData.scenarioName || "Car B";

    debugLog('📂 LOADING CAR DATA (Car B)', {
      name: finalScenarioName,
      region: scenarioData.region,
      totalCost: scenarioData.totalCost,
      annualInsurance: scenarioData.annualInsurance
    });

    // Update TylerState and restore all fields
    TylerState.carNameB = finalScenarioName;
    // Merge with current data to preserve any new fields with defaults
    setComparisonData(prev => ({ ...prev, ...scenarioData, scenarioName: finalScenarioName }));

    // Restore checkbox states
    if (scenarioData.includeFinanceScenario !== undefined) {
      setIncludeFinanceScenario(scenarioData.includeFinanceScenario);
      TylerState.car.includeFinanceScenario = scenarioData.includeFinanceScenario;
    }
    if (scenarioData.includeLeaseScenario !== undefined) {
      setIncludeLeaseScenario(scenarioData.includeLeaseScenario);
      TylerState.car.includeLeaseScenario = scenarioData.includeLeaseScenario;
    }
    updateReact();

    setInputsChanged(true);
    setShowResults(false);

    // Show success modal with timestamp
    const formattedDate = timestamp
      ? new Date(timestamp).toLocaleString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      : 'Unknown';

    setGuidanceTitle('Scenario Loaded');
    setGuidanceContent(`<strong>${finalScenarioName}</strong> loaded successfully.<br><br>Last Updated: ${formattedDate}`);
    setShowGuidanceModal(true);
  };

  const handleDeleteScenario = async (id: string) => {
    try {
      await carScenariosService.deleteCarScenario(id);
      // Reload scenarios from backend
      await loadSavedScenarios();
    } catch (error) {
      console.error('Error deleting scenario:', error);
    }
  };

  const handleComparisonToggle = (enableComparison: boolean) => {
    const currentUser = TylerState.user;

    if (enableComparison && currentUser && currentUser.tier !== 'supporter') {
      setGuidanceTitle('Supporter Feature');
      setGuidanceContent('Comparison mode requires a Supporter account. <a href="/account" style="color: #3b82f6; text-decoration: underline;">Upgrade here</a>');
      setShowGuidanceModal(true);
      setComparisonMode(false);
      return;
    }
    if (enableComparison && !currentUser) {
      setGuidanceTitle('Supporter Feature');
      setGuidanceContent('Comparison mode requires a Supporter account. <a href="/account" style="color: #3b82f6; text-decoration: underline;">Create account & upgrade</a>');
      setShowGuidanceModal(true);
      setComparisonMode(false);
      return;
    }
    setComparisonMode(enableComparison);

    // Hide results when switching modes - force user to review inputs and recalculate
    setShowResults(false);
    setInputsChanged(true);
  };

  // Professional PDF generation
  const generateProfessionalPDF = async () => {
    const currentUser = TylerState.user;
    if (!currentUser || currentUser.tier !== 'supporter') {
      setGuidanceTitle('Supporter Feature');
      setGuidanceContent('PDF export requires a Supporter account. <a href="/account" style="color: #3b82f6; text-decoration: underline;">Upgrade here</a>');
      setShowGuidanceModal(true);
      return;
    }

    try {
      const pdfService = new CarPDFService();
      // PDF service now reads from TylerState.car - only pass scenario name
      await pdfService.generateProfessionalPDF(
        null, // No longer used - reads from TylerState
        saveName || `Car Analysis ${new Date().toLocaleString()}`
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      setGuidanceTitle('PDF Generation Error');
      setGuidanceContent('There was an error generating the PDF. Please try again.');
      setShowGuidanceModal(true);
    }
  };

  const [data, setData] = useState<CarComparisonData>({
    // Scenario Names
    scenarioName: "Car A",
    comparisonScenarioName: "Car B",
    // Region Selection
    region: 'Canada',

    // Fuel Type
    fuelType: 'gasoline',

    // General Inputs
    ownershipTimeframe: 5,
    fuelEfficiency: 13.1,
    fuelCostPerUnit: 1.50,
    annualMileage: 15000,
    inflationRate: 0,

    // CO₂ Comparison
    includeCO2Comparison: false,
    isNewCar: true,
    openingMileage: 0,
    productionEmissionsCategory: 'medium',
    manufacturingEmissions: 10000, // Will be calculated from category
    
    // Input Detail Level
    useDetailedMileage: false,
    useDetailedMaintenance: false,
    
    // Enhanced Mileage Inputs (years 1-5 individual, year 6+ ongoing)
    mileageYear1: 15000,
    mileageYear2: 15000,
    mileageYear3: 15000,
    mileageYear4: 15000,
    mileageYear5: 15000,
    mileageYear6Plus: 15000,
    
    // Buy Inputs
    totalCost: 50000,
    taxes: 13,
    useTaxesPercent: true,
    fees: 0,
    cashPurchaseFees: 0,
    maintenanceCost: 2000,
    expectedValueAtEnd: 32500,
    depreciationPercent: 35,
    useDepreciationPercent: true,
    sellingCosts: 5000,
    sellingCostsPercent: 10,
    useSellingCostsPercent: true,
    
    // Enhanced Maintenance Inputs (years 1-5 individual, year 6+ ongoing)
    maintenanceYear1: 1000,
    maintenanceYear2: 1200,
    maintenanceYear3: 1500,
    maintenanceYear4: 2000,
    maintenanceYear5: 2500,
    maintenanceYear6Plus: 3000,
    
    // Finance Inputs
    downPayment: 0,
    interestRate: 5.0,
    financingYears: 5,
    financeFees: 0,
    monthlyPayment: 0,
    extraMonthlyPayment: 0,
    
    // Lease Inputs
    leaseTerm: 4,
    totalMileageAllowance: 48000,
    costPerExtraMile: 0.25,
    leasePayment: 1000,
    residualValue: 0,
    leaseFees: 3000,
    leaseOriginationFees: 0,
    leaseAPR: 6.0,
    leaseDownPayment: 0,
    endOfLeaseOption: 'choose-best',

    // General Costs
    annualInsurance: 3000,
    otherCarCosts: 0,

    // Opportunity Cost
    investmentReturnRate: 5.0,
  });

  const [comparisonData, setComparisonData] = useState<CarComparisonData>({
    // Scenario Names
    scenarioName: "Car B",
    comparisonScenarioName: "Car A",
    // Region Selection
    region: 'Canada',

    // Fuel Type
    fuelType: 'gasoline',

    // General Inputs
    ownershipTimeframe: 5,
    fuelEfficiency: 8.5,
    fuelCostPerUnit: 1.50,
    annualMileage: 15000,
    inflationRate: 0,

    // CO₂ Comparison
    includeCO2Comparison: false,
    isNewCar: true,
    openingMileage: 0,
    productionEmissionsCategory: 'medium',
    manufacturingEmissions: 10000, // Will be calculated from category

    // Input Detail Level
    useDetailedMileage: false,
    useDetailedMaintenance: false,
    
    // Enhanced Mileage Inputs (years 1-5 individual, year 6+ ongoing)
    mileageYear1: 15000,
    mileageYear2: 15000,
    mileageYear3: 15000,
    mileageYear4: 15000,
    mileageYear5: 15000,
    mileageYear6Plus: 15000,
    
    // Buy Inputs
    totalCost: 50000,
    taxes: 13,
    useTaxesPercent: true,
    fees: 0,
    cashPurchaseFees: 0,
    maintenanceCost: 2000,
    expectedValueAtEnd: 25000,
    depreciationPercent: 35,
    useDepreciationPercent: true,
    sellingCosts: 3000,
    sellingCostsPercent: 10,
    useSellingCostsPercent: true,
    
    // Enhanced Maintenance Inputs (years 1-5 individual, year 6+ ongoing)
    maintenanceYear1: 800,
    maintenanceYear2: 1000,
    maintenanceYear3: 1200,
    maintenanceYear4: 1500,
    maintenanceYear5: 1800,
    maintenanceYear6Plus: 2000,
    
    // Finance Inputs
    downPayment: 0,
    interestRate: 5.0,
    financingYears: 5,
    financeFees: 0,
    monthlyPayment: 0,
    extraMonthlyPayment: 0,
    
    // Lease Inputs
    leaseTerm: 4,
    totalMileageAllowance: 48000,
    costPerExtraMile: 0.25,
    leasePayment: 1000,
    residualValue: 0,
    leaseFees: 3000,
    leaseOriginationFees: 0,
    leaseAPR: 6.0,
    leaseDownPayment: 0,
    endOfLeaseOption: 'choose-best',

    // General Costs
    annualInsurance: 2000,
    otherCarCosts: 0,

    // Opportunity Cost
    investmentReturnRate: 5.0,
  });

  // State for detailed analysis sections (mutually exclusive)
  const [activeAnalysisSection, setActiveAnalysisSection] = useState<AnalysisSection>(null);
  
  // Navigation state (matching retirement calculator pattern)
  const [activeSection, setActiveSection] = useState('general');
  const [isInputSectionVisible, setIsInputSectionVisible] = useState(true);

  // Process guest scenario if provided
  useEffect(() => {
    if (guestScenario && guestScenario.data) {
      setData(guestScenario.data);
      if (guestScenario.data.scenarioName) {
        setSaveName(guestScenario.data.scenarioName);
      }
    }
  }, [guestScenario]);

  // Sync with global country context - update region and defaults when country changes
  useEffect(() => {
    const newRegion: 'Canada' | 'US' = isUSA ? 'US' : 'Canada';
    const newTaxRate = isUSA ? getAverageUSSalesTax() : CA_PROVINCE_TAX_RATES['ON'].salesTax;
    const newFuelCost = isUSA ? 3.50 : 1.50;
    const newFuelEfficiency = isUSA ? 28 : 13.1; // MPG for US, L/100km for Canada

    setData(prev => ({
      ...prev,
      region: newRegion,
      taxes: newTaxRate,
      fuelCostPerUnit: newFuelCost,
      fuelEfficiency: newFuelEfficiency,
    }));
    setComparisonData(prev => ({
      ...prev,
      region: newRegion,
      taxes: newTaxRate,
      fuelCostPerUnit: newFuelCost,
      fuelEfficiency: isUSA ? 28 : 8.5,
    }));
  }, [isUSA]);

  // Trigger initial lease calculation when component mounts
  useEffect(() => {
    // Small delay to ensure data is set
    setTimeout(() => autoCalculateLeaseValue(data), 100);
  }, []);

  // Cleanup function to clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (guidanceTimeoutRef.current) {
        clearTimeout(guidanceTimeoutRef.current);
      }
      if (leaseCalculationTimeoutRef.current) {
        clearTimeout(leaseCalculationTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (field: keyof CarComparisonData, value: any) => {
    // Track input changes for analytics
    trackInput(field, value.toString());
    
    // Validation checks
    if (field === 'downPayment') {
      const maxDownPayment = data.totalCost + (data.useTaxesPercent ? data.totalCost * data.taxes / 100 : data.taxes) + data.fees;
      if (value > maxDownPayment) {
        showGuidance(
          'Invalid Down Payment',
          `Down payment cannot exceed the total cost of the car plus taxes and fees (${formatCurrency(maxDownPayment)}).<br><br>Please reduce your down payment to continue.`
        );
        trackError(`Down payment validation failed: ${value} > ${maxDownPayment}`, 'downPayment', field, value.toString());
        return;
      }
    }
    
    if (field === 'leaseDownPayment') {
      const maxLeaseDownPayment = data.totalCost + (data.useTaxesPercent ? data.totalCost * data.taxes / 100 : data.taxes) + data.fees;
      if (value > maxLeaseDownPayment) {
        showGuidance(
          'Invalid Lease Down Payment',
          `Lease down payment cannot exceed the total cost of the car plus taxes and fees (${formatCurrency(maxLeaseDownPayment)}).<br><br>Please reduce your lease down payment to continue.`
        );
        return;
      }
      
      // Additional lease validation
      if (leaseInputMode === 'payment-apr' || leaseInputMode === 'payment-residual') {
        const capitalizedCost = data.totalCost + (data.useTaxesPercent ? data.totalCost * data.taxes / 100 : data.taxes) + data.fees - value;
        if (capitalizedCost <= 0) {
          showGuidance(
            'Invalid Lease Down Payment',
            'Lease down payment is too high. The capitalized cost (car price + taxes + fees - down payment) must be positive.<br><br>Please reduce your lease down payment.'
          );
          return;
        }
        
        // Check if residual value would be greater than capitalized cost
        if (data.residualValue > capitalizedCost) {
          showGuidance(
            'Invalid Residual Value',
            'Residual value cannot be greater than the capitalized cost (car price + taxes + fees - down payment).<br><br>Please reduce your residual value or increase your down payment.'
          );
          return;
        }
      }
    }
    
    let updatedData: CarComparisonData;
    setData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Auto-adjust values when region changes
      if (field === 'region') {
        if (value === 'Canada') {
          newData.fuelEfficiency = 13.1;
          newData.fuelCostPerUnit = 1.50;
        } else {
          newData.fuelCostPerUnit = 3.50;
        }
      }

      updatedData = newData;
      return newData;
    });

    // Mark that inputs have changed and hide results
    setInputsChanged(true);
    setShowResults(false);

    // Auto-calculate lease values when relevant inputs change
    if (['leasePayment', 'leaseAPR', 'residualValue', 'totalCost', 'taxes', 'fees', 'leaseFees', 'leaseTerm', 'leaseDownPayment'].includes(field)) {
      const leaseData = {
        ...data,
        [field]: value
      };
      const shouldAutoCalculate = (
        (field === 'leasePayment' && leaseInputMode === 'payment-apr') ||
        (field === 'leaseAPR' && leaseInputMode === 'payment-residual') ||
        (field === 'residualValue' && leaseInputMode === 'payment-residual') ||
        (field === 'leasePayment' && leaseInputMode === 'apr-residual') ||
        (field === 'leaseAPR' && leaseInputMode === 'apr-residual') ||
        (['totalCost', 'taxes', 'fees', 'leaseFees', 'leaseTerm', 'leaseDownPayment'].includes(field) && 
         ['payment-apr', 'payment-residual', 'apr-residual'].includes(leaseInputMode))
      );
      
      if (shouldAutoCalculate) {
        if (field !== 'leasePayment' && field !== 'leaseAPR' && field !== 'residualValue') {
          autoCalculateLeaseValue(leaseData);
        }
      }
    }
  };

  const handleComparisonInputChange = (field: keyof CarComparisonData, value: any) => {
    // Validation checks for comparison data
    if (field === 'downPayment') {
      const maxDownPayment = comparisonData.totalCost + (comparisonData.useTaxesPercent ? comparisonData.totalCost * comparisonData.taxes / 100 : comparisonData.taxes) + comparisonData.fees;
      if (value > maxDownPayment) {
        showGuidance(
          'Invalid Down Payment',
          `Down payment cannot exceed the total cost of the car plus taxes and fees (${formatCurrency(maxDownPayment)}).<br><br>Please reduce your down payment to continue.`
        );
        return;
      }
    }
    
    if (field === 'leaseDownPayment') {
      const maxLeaseDownPayment = comparisonData.totalCost + (comparisonData.useTaxesPercent ? comparisonData.totalCost * comparisonData.taxes / 100 : comparisonData.taxes) + comparisonData.fees;
      if (value > maxLeaseDownPayment) {
        showGuidance(
          'Invalid Lease Down Payment',
          `Lease down payment cannot exceed the total cost of the car plus taxes and fees (${formatCurrency(maxLeaseDownPayment)}).<br><br>Please reduce your lease down payment to continue.`
        );
        return;
      }
      
      // Additional lease validation
      if (leaseInputMode === 'payment-apr' || leaseInputMode === 'payment-residual') {
        const capitalizedCost = comparisonData.totalCost + (comparisonData.useTaxesPercent ? comparisonData.totalCost * comparisonData.taxes / 100 : comparisonData.taxes) + comparisonData.fees - value;
        if (capitalizedCost <= 0) {
          showGuidance(
            'Invalid Lease Down Payment',
            'Lease down payment is too high. The capitalized cost (car price + taxes + fees - down payment) must be positive.<br><br>Please reduce your lease down payment.'
          );
          return;
        }
        
        // Check if residual value would be greater than capitalized cost
        if (comparisonData.residualValue > capitalizedCost) {
          showGuidance(
            'Invalid Residual Value',
            'Residual value cannot be greater than the capitalized cost (car price + taxes + fees - down payment).<br><br>Please reduce your residual value or increase your down payment.'
          );
          return;
        }
      }
    }
    
    setComparisonData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Auto-adjust values when region changes
      if (field === 'region') {
        if (value === 'Canada') {
          newData.fuelEfficiency = 8.5;
          newData.fuelCostPerUnit = 1.50;
        } else {
          newData.fuelEfficiency = 28;
          newData.fuelCostPerUnit = 3.50;
        }
      }
      
      return newData;
    });

    // Mark that inputs have changed and hide results
    setInputsChanged(true);
    setShowResults(false);
  };

  // Auto-calculate the third lease value based on input mode
  const autoCalculateLeaseValue = (leaseData: CarComparisonData) => {
    const capitalizedCost = leaseData.totalCost + (leaseData.useTaxesPercent ? leaseData.totalCost * leaseData.taxes / 100 : leaseData.taxes) + leaseData.fees - leaseData.leaseDownPayment;
    
    if (capitalizedCost <= 0) return;

    let newData = {...leaseData}
    
    switch (leaseInputMode) {
      case 'payment-apr':
        newData.residualValue = Math.round(calculateLeaseResidualValue(leaseData));
        break;
      case 'payment-residual':
        newData.leaseAPR = Math.round(calculateLeaseAPR(leaseData)*100)/100;
        break;
      case 'apr-residual':
        newData.leasePayment = Math.round(calculateLeasePayment(leaseData)*100)/100;
        break;
    }
    
    setData(newData);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(data.region === 'Canada' ? 'en-CA' : 'en-US', {
      style: 'currency',
      currency: data.region === 'Canada' ? 'CAD' : 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyWithColor = (value: number) => {
    const isNegative = value < 0;
    const displayValue = Math.abs(value);
    const formattedValue = formatCurrency(displayValue);

    return (
      <span style={{ color: isNegative ? '#27ae60' : '#e74c3c' }}>
        {formattedValue}
      </span>
    );
  };

  const formatCostPerKilometer = (value: number, totalCost: number) => {
    const decimals = Math.abs(totalCost) < 100 ? 2 : 0;

    const formattedValue = new Intl.NumberFormat(data.region === 'Canada' ? 'en-CA' : 'en-US', {
      style: 'currency',
      currency: data.region === 'Canada' ? 'CAD' : 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(Math.abs(value));

    const isNegative = value < 0;

    return (
      <span style={{ color: isNegative ? '#27ae60' : '#e74c3c' }}>
        {formattedValue}
      </span>
    );
  };

  const validateInputs = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Validate General Inputs
    if (data.ownershipTimeframe <= 0) {
      errors.push('Ownership timeframe must be greater than 0');
    }
    if (data.fuelEfficiency <= 0) {
      errors.push('Fuel efficiency must be greater than 0');
    }
    if (data.annualMileage <= 0 && !data.useDetailedMileage) {
      errors.push('Annual mileage must be greater than 0');
    }

    // Validate Buy Scenario
    if (data.totalCost <= 0) {
      errors.push('Total cost must be greater than 0');
    }

    // Validate Finance Scenario
    if (data.downPayment < 0) {
      errors.push('Down payment cannot be negative');
    }
    if (data.interestRate < 0) {
      errors.push('Interest rate cannot be negative');
    }
    if (data.financingYears <= 0) {
      errors.push('Financing years must be greater than 0');
    }

    // Validate Lease Scenario
    if (data.leaseTerm <= 0) {
      errors.push('Lease term must be greater than 0');
    }
    if (data.leaseDownPayment < 0) {
      errors.push('Lease down payment cannot be negative');
    }
    if (data.leasePayment < 0) {
      errors.push('Lease payment cannot be negative');
    }

    // Validate comparison mode inputs if enabled
    if (comparisonMode) {
      if (comparisonData.totalCost <= 0) {
        errors.push('Car B total cost must be greater than 0');
      }
      if (comparisonData.ownershipTimeframe <= 0) {
        errors.push('Car B ownership timeframe must be greater than 0');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Store inputs and outputs in TylerState for debugging and testing
  const updateTylerStateCarData = () => {
    // Car A inputs and outputs
    TylerState.car.carA.inputs = { ...data };
    const resultsA = calculateResults(data);

    // Calculate derived values
    const buyTotalCost = Math.abs(resultsA.buy.finalBalance);
    const financeTotalCost = Math.abs(resultsA.finance.finalBalance);
    const leaseTotalCost = Math.abs(resultsA.lease.finalBalance);
    const totalMiles = Math.max(1, data.annualMileage * data.ownershipTimeframe);
    const totalMonths = Math.max(1, data.ownershipTimeframe * 12);

    TylerState.car.carA.outputs = {
      buy: {
        totalCost: buyTotalCost,
        costPerKm: buyTotalCost / totalMiles,
        costPerMonth: buyTotalCost / totalMonths,
        finalBalance: resultsA.buy.finalBalance,
        yearlyData: resultsA.buy.yearlyData
      },
      finance: {
        totalCost: financeTotalCost,
        costPerKm: financeTotalCost / totalMiles,
        costPerMonth: financeTotalCost / totalMonths,
        finalBalance: resultsA.finance.finalBalance,
        monthlyPayment: calculateMonthlyPayment(data),
        totalInterest: calculateMonthlyPayment(data) * data.financingYears * 12 - (data.totalCost + (data.useTaxesPercent ? data.totalCost * data.taxes / 100 : data.taxes) + data.fees - data.downPayment),
        yearlyData: resultsA.finance.yearlyData
      },
      lease: {
        totalCost: leaseTotalCost,
        costPerKm: leaseTotalCost / totalMiles,
        costPerMonth: leaseTotalCost / totalMonths,
        finalBalance: resultsA.lease.finalBalance,
        monthlyLeasePayment: data.leasePayment,
        yearlyData: resultsA.lease.yearlyData
      }
    };

    // If comparison mode, store Car B data
    if (comparisonMode) {
      TylerState.car.carB.inputs = { ...comparisonData };
      const resultsB = calculateResults(comparisonData);

      const buyTotalCostB = Math.abs(resultsB.buy.finalBalance);
      const financeTotalCostB = Math.abs(resultsB.finance.finalBalance);
      const leaseTotalCostB = Math.abs(resultsB.lease.finalBalance);
      const totalMilesB = Math.max(1, comparisonData.annualMileage * comparisonData.ownershipTimeframe);
      const totalMonthsB = Math.max(1, comparisonData.ownershipTimeframe * 12);

      TylerState.car.carB.outputs = {
        buy: {
          totalCost: buyTotalCostB,
          costPerKm: buyTotalCostB / totalMilesB,
          costPerMonth: buyTotalCostB / totalMonthsB,
          finalBalance: resultsB.buy.finalBalance,
          yearlyData: resultsB.buy.yearlyData
        },
        finance: {
          totalCost: financeTotalCostB,
          costPerKm: financeTotalCostB / totalMilesB,
          costPerMonth: financeTotalCostB / totalMonthsB,
          finalBalance: resultsB.finance.finalBalance,
          monthlyPayment: calculateMonthlyPayment(comparisonData),
          totalInterest: calculateMonthlyPayment(comparisonData) * comparisonData.financingYears * 12 - (comparisonData.totalCost + (comparisonData.useTaxesPercent ? comparisonData.totalCost * comparisonData.taxes / 100 : comparisonData.taxes) + comparisonData.fees - comparisonData.downPayment),
          yearlyData: resultsB.finance.yearlyData
        },
        lease: {
          totalCost: leaseTotalCostB,
          costPerKm: leaseTotalCostB / totalMilesB,
          costPerMonth: leaseTotalCostB / totalMonthsB,
          finalBalance: resultsB.lease.finalBalance,
          monthlyLeasePayment: comparisonData.leasePayment,
          yearlyData: resultsB.lease.yearlyData
        }
      };

      // Store comparison results (calculateComparisonResults only takes one parameter - the comparison data)
      const compResults = calculateComparisonResults(comparisonData);

      // Determine winner by comparing total costs
      let winner = '';
      if (buyTotalCost < buyTotalCostB && buyTotalCost < financeTotalCostB && buyTotalCost < leaseTotalCostB) {
        winner = 'Car A - Buy';
      } else if (financeTotalCost < buyTotalCostB && financeTotalCost < financeTotalCostB && financeTotalCost < leaseTotalCostB) {
        winner = 'Car A - Finance';
      } else if (leaseTotalCost < buyTotalCostB && leaseTotalCost < financeTotalCostB && leaseTotalCost < leaseTotalCostB) {
        winner = 'Car A - Lease';
      } else if (buyTotalCostB < financeTotalCostB && buyTotalCostB < leaseTotalCostB) {
        winner = 'Car B - Buy';
      } else if (financeTotalCostB < leaseTotalCostB) {
        winner = 'Car B - Finance';
      } else {
        winner = 'Car B - Lease';
      }

      TylerState.car.comparison = {
        winner,
        results: compResults
      };
    }

    // Trigger React updates for components watching TylerState
    updateReact();
  };

  const handleCalculate = () => {
    autoCalculateLeaseValue (data);
    const validation = validateInputs();

    if (!validation.isValid) {
      // Show validation errors
      setGuidanceTitle('Validation Errors');
      setGuidanceContent(
        'Please fix the following errors before calculating:<br><br>' +
        validation.errors.map(err => `• ${err}`).join('<br>')
      );
      setShowGuidanceModal(true);
      return;
    }

    //UpdateData
    if (!data.useDetailedMaintenance){
      data.maintenanceYear1 = data.maintenanceCost;
      data.maintenanceYear2 = data.maintenanceCost;
      data.maintenanceYear3 = data.maintenanceCost;
      data.maintenanceYear4 = data.maintenanceCost;
      data.maintenanceYear5 = data.maintenanceCost;
      data.maintenanceYear6Plus = data.maintenanceCost;    
    }

    // Update TylerState with all calculation data
    updateTylerStateCarData();

    //Set some more rules
    TylerState.car.includeFinanceScenario = includeFinanceScenario;
    TylerState.car.includeLeaseScenario = includeLeaseScenario;


    setInputsChanged(false);
    setShowDisclaimerModal(true);
  };

  const showGuidance = (title: string, content: string) => {
    // Clear any existing timeout to prevent duplicate modals
    if (guidanceTimeoutRef.current) {
      clearTimeout(guidanceTimeoutRef.current);
    }
    
    // Prevent rapid successive modal triggers
    if (showGuidanceModal) {
      return;
    }
    
    setGuidanceTitle(title);
    setGuidanceContent(content);
    setShowGuidanceModal(true);
    
    // Set a timeout to prevent rapid re-triggers
    guidanceTimeoutRef.current = setTimeout(() => {
      guidanceTimeoutRef.current = null;
    }, 500);
  };

  const renderCostBreakdown = (scenario: BreakdownScenario, period: BreakdownPeriod = 'total', carData?: CarComparisonData) => {
    const targetData = carData || data; // Use provided data or default to main data
    const results = calculateResults(targetData);
    const scenarioData = results[scenario];
    const opportunityCost = (scenarioData.yearlyData as any[]).reduce((sum, year) => sum + year.investmentReturn, 0);

    const applyInflation = (baseValue: number, year: number) => {
      return baseValue * Math.pow(1 + targetData.inflationRate / 100, year - 1);
    };

    const adjustForPeriod = (value: number) => {
      if (period === 'monthly') {
        return value / (targetData.ownershipTimeframe * 12);
      } else if (period === 'annual') {
        return value / targetData.ownershipTimeframe;
      } else if (period === 'per-km') {
        return value / (targetData.annualMileage * targetData.ownershipTimeframe);
      }
      return value;
    };

    const getPeriodLabel = () => {
      if (period === 'monthly') {
        return `Monthly Average - ${targetData.ownershipTimeframe * 12} months`;
      } else if (period === 'annual') {
        return `Annual Average - ${targetData.ownershipTimeframe} years`;
      } else if (period === 'per-km') {
        return `Cost per Kilometer`;
      }
      return `Total - ${targetData.ownershipTimeframe} years`;
    };

    const breakdownItems = [
      {
        name: scenario === 'buy' ? 'Upfront Purchase Cost' : scenario === 'finance' ? 'Monthly Payments' : 'Monthly Payments',
        value: adjustForPeriod(scenario === 'buy' ? (targetData.totalCost + (targetData.useTaxesPercent ? targetData.totalCost * targetData.taxes / 100 : targetData.taxes) + targetData.fees) : scenario === 'finance' ? ((results.finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + Math.abs(year.monthlyCosts), 0)) : (targetData.leasePayment * targetData.leaseTerm * 12)),
        color: '#e74c3c'
      },
      ...(scenario === 'lease' && targetData.leaseDownPayment > 0 ? [{
        name: 'Lease Down Payment',
        value: adjustForPeriod(targetData.leaseDownPayment),
        color: '#e74c3c'
      }] : []),
      ...(scenario === 'lease' ? [{
        name: 'Residual Value Payment',
        value: adjustForPeriod(scenario === 'lease' && (targetData.leaseTerm < targetData.ownershipTimeframe || targetData.leaseTerm === targetData.ownershipTimeframe) ? (targetData.residualValue + targetData.leaseFees) : 0),
        color: '#e74c3c'
      }] : []),
      {
        name: 'Fuel Costs',
        value: adjustForPeriod((() => {
          if (targetData.region === 'Canada') {
            return (targetData.annualMileage / 100) * targetData.fuelEfficiency * targetData.fuelCostPerUnit;
          } else {
            return (targetData.annualMileage / targetData.fuelEfficiency) * targetData.fuelCostPerUnit;
          }
        })() * targetData.ownershipTimeframe),
        color: '#e74c3c'
      },
      {
        name: 'Insurance Costs',
        value: adjustForPeriod(targetData.annualInsurance * targetData.ownershipTimeframe),
        color: '#e74c3c'
      },
      {
        name: 'Other Costs',
        value: adjustForPeriod(applyInflation(targetData.otherCarCosts, 1) * targetData.ownershipTimeframe),
        color: '#e74c3c'
      },
      ...(scenario === 'lease' ? [{
        name: 'Lease Overage Costs',
        value: adjustForPeriod(scenario === 'lease' ? (results.lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + Math.abs(year.overageCost), 0) : 0),
        color: '#e74c3c'
      }] : []),
      {
        name: 'Opportunity Cost (Investments)',
        value: adjustForPeriod(opportunityCost),
        color: '#e74c3c'
      },
      {
        name: 'Resale Value',
        value: adjustForPeriod(scenario === 'lease' && (targetData.leaseTerm < targetData.ownershipTimeframe || targetData.leaseTerm === targetData.ownershipTimeframe) ? -(results.lease.yearlyData[results.lease.yearlyData.length - 1] as any)?.sellCar || 0 : scenario === 'buy' || scenario === 'finance' ? -(results.buy.yearlyData[results.buy.yearlyData.length - 1] as any)?.sellCar || 0 : 0),
        color: '#27ae60'
      }
    ];

    const totalCost = adjustForPeriod(scenarioData.finalBalance);
    const totalKilometers = targetData.annualMileage * targetData.ownershipTimeframe;
    const costPerKilometer = totalCost / totalKilometers;

    return (
      <div className="cost-breakdown">
        <h4>Cost of Ownership Breakdown - {scenario.charAt(0).toUpperCase() + scenario.slice(1)} ({getPeriodLabel()})</h4>
        <div className="breakdown-container">
          {breakdownItems.map((item, index) => (
            <div key={index} className="breakdown-item">
              <div className="item-details">
                <span className="item-name">{item.name}:</span>
                <span className="item-value">
                  {period === 'per-km' ? formatCostPerKilometer(item.value, totalCost) : formatCurrencyWithColor(item.value)}
                </span>
              </div>
              <div className={`cost-bar ${item.color === '#e74c3c' ? 'costs-section' : item.color === '#27ae60' ? 'income-section' : 'opportunity-cost-section'}`}>
                <div
                  className={`bar-container ${item.color === '#e74c3c' ? 'cost-bar' : item.color === '#27ae60' ? 'income-bar' : 'opportunity-cost-bar'}`}
                  style={{
                    width: `${Math.abs(item.value) / Math.max(...breakdownItems.map(i => Math.abs(i.value))) * 100}%`,
                    backgroundColor: item.color
                  }}
                />
              </div>
            </div>
          ))}

          {/* Total Cost Display */}
          <div className="breakdown-item total-cost">
            <div className="item-details">
              <span className="item-name">Total Cost:</span>
              <span className="item-value">
                {period === 'per-km' ? formatCostPerKilometer(totalCost, totalCost) : formatCurrencyWithColor(totalCost)}
              </span>
            </div>
            <div className="total-bar">
              <div
                className="bar-container total-bar"
                style={{
                  width: '100%',
                  backgroundColor: '#2c3e50'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle section navigation (matching retirement calculator pattern)
  const handleSectionClick = async (sectionId: string) => {
    // Auto-save before switching sections
    const currentUser = TylerState.user;

    if (currentUser) {
      try {
        // Use data.scenarioName as source of truth (persists across reloads)
        const name = (data.scenarioName || '').trim();
        const finalName = name || 'Temporary';

        const cap = getScenarioCap(currentUser.tier || 'free');
        const existingScenario = savedScenarios.find(s => s.name.toLowerCase() === finalName.toLowerCase());

        if (existingScenario || savedScenarios.length < cap) {
          const updatedData = { ...data, scenarioName: finalName };

          debugLog('🔍 SAVING DATA', {
            finalName,
            totalCost: data.totalCost,
            downPayment: data.downPayment,
            annualInsurance: data.annualInsurance,
            fuelEfficiency: data.fuelEfficiency,
            fullDataKeys: Object.keys(data).slice(0, 10)
          });

          if (existingScenario) {
            await carScenariosService.updateCarScenario(existingScenario.id, {
              name: finalName,
              description: saveDescription.trim() || undefined,
              data: updatedData
            });
          } else {
            await carScenariosService.saveCarScenario({
              name: finalName,
              description: saveDescription.trim() || undefined,
              data: updatedData
            });
          }
          await loadSavedScenarios();
        }
      } catch (error) {
        console.error('Auto-save on section change failed:', error);
      }
    }

    if (activeSection === sectionId) {
      setIsInputSectionVisible(!isInputSectionVisible);
    } else {
      setActiveSection(sectionId);
      setIsInputSectionVisible(true);
    }

    // Auto-scroll to top on section change
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Define sections (matching retirement calculator pattern)
  const sections = [
    {
      id: 'general',
      label: 'General Information',
      render: () => (
        comparisonMode ? (
          <div className="comparison-layout">
            <div className="comparison-column">
              <h4>{data.scenarioName || 'Car A'}</h4>
              <GeneralInputs
                user={TylerState.user}
                data={data}
                onInputChange={handleInputChange}
                showGuidance={showGuidance}
                comparisonMode={true}
                onLoadScenarioToA={handleLoadScenarioToA}
                onLoadScenarioToB={handleLoadScenarioToB}
                onSaveScenario={onSaveScenario}
                side="A"
              />
            </div>
            <div className="comparison-column">
              <h4>{comparisonData.scenarioName || 'Car B'}</h4>
              <GeneralInputs
                user={TylerState.user}
                data={comparisonData}
                onInputChange={handleComparisonInputChange}
                showGuidance={showGuidance}
                comparisonMode={true}
                onLoadScenarioToA={handleLoadScenarioToA}
                onLoadScenarioToB={handleLoadScenarioToB}
                onSaveScenario={onSaveScenario}
                side="B"
              />
            </div>
          </div>
        ) : (
          <GeneralInputs
            user={TylerState.user}
            data={data}
            onInputChange={handleInputChange}
            showGuidance={showGuidance}
            comparisonMode={false}
            onLoadScenarioToA={handleLoadScenarioToA}
            onSaveScenario={onSaveScenario}
          />
        )
      )
    },
    {
      id: 'purchase',
      label: 'Purchase Details',
      render: () => (
        comparisonMode ? (
          <div className="comparison-layout">
            <div className="comparison-column">
              <h4>{data.scenarioName || 'Car A'}</h4>
              <BuyInputs
                data={data}
                onInputChange={handleInputChange}
                showGuidance={showGuidance}
                formatCurrency={formatCurrency}
              />
            </div>
            <div className="comparison-column">
              <h4>{comparisonData.scenarioName || 'Car B'}</h4>
              <BuyInputs
                data={comparisonData}
                onInputChange={handleComparisonInputChange}
                showGuidance={showGuidance}
                formatCurrency={formatCurrency}
              />
            </div>
          </div>
        ) : (
          <BuyInputs
            data={data}
            onInputChange={handleInputChange}
            showGuidance={showGuidance}
            formatCurrency={formatCurrency}
          />
        )
      )
    },
    {
      id: 'financing',
      label: 'Financing Options',
      render: () => (
        comparisonMode ? (
          <div className="comparison-layout">
            <div className="comparison-column">
              <h4>{data.scenarioName || 'Car A'}</h4>
              <FinanceInputs
                data={data}
                onInputChange={handleInputChange}
                showGuidance={showGuidance}
                formatCurrency={formatCurrency}
                includeFinanceScenario={includeFinanceScenario}
                setIncludeFinanceScenario={setIncludeFinanceScenario}
              />
            </div>
            <div className="comparison-column">
              <h4>{comparisonData.scenarioName || 'Car B'}</h4>
              <FinanceInputs
                data={comparisonData}
                onInputChange={handleComparisonInputChange}
                showGuidance={showGuidance}
                formatCurrency={formatCurrency}
                includeFinanceScenario={includeFinanceScenario}
                setIncludeFinanceScenario={setIncludeFinanceScenario}
              />
            </div>
          </div>
        ) : (
          <FinanceInputs
            data={data}
            onInputChange={handleInputChange}
            showGuidance={showGuidance}
            formatCurrency={formatCurrency}
            includeFinanceScenario={includeFinanceScenario}
            setIncludeFinanceScenario={setIncludeFinanceScenario}
          />
        )
      )
    },
    {
      id: 'leasing',
      label: 'Leasing Options',
      render: () => (
        comparisonMode ? (
          <div className="comparison-layout">
            <div className="comparison-column">
              <h4>{data.scenarioName || 'Car A'}</h4>
              <LeaseInputs
                data={data}
                onInputChange={handleInputChange}
                showGuidance={showGuidance}
                formatCurrency={formatCurrency}
                leaseInputMode={leaseInputMode}
                setLeaseInputMode={setLeaseInputMode}
                includeLeaseScenario={includeLeaseScenario}
                setIncludeLeaseScenario={setIncludeLeaseScenario}
              />
            </div>
            <div className="comparison-column">
              <h4>{comparisonData.scenarioName || 'Car B'}</h4>
              <LeaseInputs
                data={comparisonData}
                onInputChange={handleComparisonInputChange}
                showGuidance={showGuidance}
                formatCurrency={formatCurrency}
                leaseInputMode={leaseInputMode}
                setLeaseInputMode={setLeaseInputMode}
                includeLeaseScenario={includeLeaseScenario}
                setIncludeLeaseScenario={setIncludeLeaseScenario}
              />
            </div>
          </div>
        ) : (
          <LeaseInputs
            data={data}
            onInputChange={handleInputChange}
            showGuidance={showGuidance}
            formatCurrency={formatCurrency}
            leaseInputMode={leaseInputMode}
            setLeaseInputMode={setLeaseInputMode}
            includeLeaseScenario={includeLeaseScenario}
            setIncludeLeaseScenario={setIncludeLeaseScenario}
          />
        )
      )
    },
    {
      id: 'costs',
      label: 'Ongoing Costs',
      render: () => (
        comparisonMode ? (
          <div className="comparison-layout">
            <div className="comparison-column">
              <h4>{data.scenarioName || 'Car A'}</h4>
              <GeneralCosts
                data={data}
                onInputChange={handleInputChange}
                showGuidance={showGuidance}
                comparisonMode={comparisonMode}
              />
            </div>
            <div className="comparison-column">
              <h4>{comparisonData.scenarioName || 'Car B'}</h4>
              <GeneralCosts
                data={comparisonData}
                onInputChange={handleComparisonInputChange}
                showGuidance={showGuidance}
                comparisonMode={comparisonMode}
              />
            </div>
          </div>
        ) : (
          <GeneralCosts
            data={data}
            onInputChange={handleInputChange}
            showGuidance={showGuidance}
            comparisonMode={comparisonMode}
          />
        )
      )
    }
  ];

  // Add results sections if calculation has been done
  const resultsSection = showResults && !inputsChanged ? [
    {
      id: 'results-summary',
      label: 'Results Summary',
      render: () => (
        <div>
          {/* Main Results Only */}
          {comparisonMode ? (
            <ComparisonResults
              data={data}
              comparisonData={comparisonData}
              formatCurrency={formatCurrency}
            />
          ) : (
            <SingleCarResults
              data={data}
              formatCurrency={formatCurrency}
            />
          )}
        </div>
      )
    },
    {
      id: 'cost-breakdown-table',
      label: 'Cost Breakdown Table',
      render: () => (
        <div style={{ padding: '2rem' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>Year-by-Year Cost Breakdown</h2>
          <p style={{ marginBottom: '2rem', color: '#666' }}>
            Detailed breakdown of all ownership costs by category, similar to industry-standard total cost of ownership analyses.
          </p>

          {/* Buy Scenario Table */}
          <CostBreakdownTable
            data={data}
            comparisonData={comparisonMode ? comparisonData : undefined}
            formatCurrency={formatCurrency}
            scenario="buy"
          />

          {/* Finance Scenario Table (if enabled) */}
          {TylerState.car.includeFinanceScenario ? (
            <CostBreakdownTable
              data={data}
              comparisonData={comparisonMode ? comparisonData : undefined}
              formatCurrency={formatCurrency}
              scenario="finance"
            />
          ) : (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px', marginTop: '1rem' }}>
              Enable "Include Finance Scenario" in inputs to see financing cost breakdown.
            </div>
          )}

          {/* Lease Scenario Table (if enabled) */}
          {TylerState.car.includeLeaseScenario ? (
            <CostBreakdownTable
              data={data}
              comparisonData={comparisonMode ? comparisonData : undefined}
              formatCurrency={formatCurrency}
              scenario="lease"
            />
          ) : (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px', marginTop: '1rem' }}>
              Enable "Include Lease Scenario" in inputs to see leasing cost breakdown.
            </div>
          )}
        </div>
      )
    },
    {
      id: 'detailed-cash-flows',
      label: 'Detailed Cash Flows',
      render: () => (
        <DetailedAnalysis
          data={data}
          comparisonData={comparisonMode ? comparisonData : undefined}
          activeAnalysisSection={'cash-flows'}
          setActiveAnalysisSection={setActiveAnalysisSection}
          selectedBreakdownScenario={selectedBreakdownScenario}
          setSelectedBreakdownScenario={setSelectedBreakdownScenario}
          selectedBreakdownPeriod={selectedBreakdownPeriod}
          setSelectedBreakdownPeriod={setSelectedBreakdownPeriod}
          formatCurrency={formatCurrency}
          renderCostBreakdown={renderCostBreakdown}
          generateProfessionalPDF={generateProfessionalPDF}
          user={TylerState.user}
        />
      )
    },
    {
      id: 'cost-of-ownership',
      label: 'Cost of Ownership',
      render: () => (
        <DetailedAnalysis
          data={data}
          comparisonData={comparisonMode ? comparisonData : undefined}
          activeAnalysisSection={'cost-of-ownership'}
          setActiveAnalysisSection={setActiveAnalysisSection}
          selectedBreakdownScenario={selectedBreakdownScenario}
          setSelectedBreakdownScenario={setSelectedBreakdownScenario}
          selectedBreakdownPeriod={selectedBreakdownPeriod}
          setSelectedBreakdownPeriod={setSelectedBreakdownPeriod}
          formatCurrency={formatCurrency}
          renderCostBreakdown={renderCostBreakdown}
          generateProfessionalPDF={generateProfessionalPDF}
          user={TylerState.user}
        />
      )
    },
    {
      id: 'resale-value',
      label: 'Resale Value',
      render: () => (
        <DetailedAnalysis
          data={data}
          comparisonData={comparisonMode ? comparisonData : undefined}
          activeAnalysisSection={'resale-value'}
          setActiveAnalysisSection={setActiveAnalysisSection}
          selectedBreakdownScenario={selectedBreakdownScenario}
          setSelectedBreakdownScenario={setSelectedBreakdownScenario}
          selectedBreakdownPeriod={selectedBreakdownPeriod}
          setSelectedBreakdownPeriod={setSelectedBreakdownPeriod}
          formatCurrency={formatCurrency}
          renderCostBreakdown={renderCostBreakdown}
          generateProfessionalPDF={generateProfessionalPDF}
          user={TylerState.user}
        />
      )
    },
    {
      id: 'annual-cost-graph',
      label: 'Annual Cost Graph',
      render: () => (
        <DetailedAnalysis
          data={data}
          comparisonData={comparisonMode ? comparisonData : undefined}
          activeAnalysisSection={'annual-cost-graph'}
          setActiveAnalysisSection={setActiveAnalysisSection}
          selectedBreakdownScenario={selectedBreakdownScenario}
          setSelectedBreakdownScenario={setSelectedBreakdownScenario}
          selectedBreakdownPeriod={selectedBreakdownPeriod}
          setSelectedBreakdownPeriod={setSelectedBreakdownPeriod}
          formatCurrency={formatCurrency}
          renderCostBreakdown={renderCostBreakdown}
          generateProfessionalPDF={generateProfessionalPDF}
          user={TylerState.user}
        />
      )
    },
    {
      id: 'print-to-pdf',
      label: 'Print to PDF',
      render: () => (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h3>Generate Professional Report</h3>
          <p>Create a comprehensive PDF report of your car comparison analysis.</p>
          <button 
            onClick={generateProfessionalPDF}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '1rem 2rem',
              fontSize: '1rem',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            📄 Generate PDF Report
          </button>
        </div>
      )
    }
  ] : [];
  if (showResults && !inputsChanged && TylerState.car.includeLeaseScenario){
    resultsSection.push(
          {
      id: 'lease-buyout',
      label: 'Lease Buyout',
      render: () => (
        <DetailedAnalysis
          data={data}
          comparisonData={comparisonMode ? comparisonData : undefined}
          activeAnalysisSection={'lease-buyout'}
          setActiveAnalysisSection={setActiveAnalysisSection}
          selectedBreakdownScenario={selectedBreakdownScenario}
          setSelectedBreakdownScenario={setSelectedBreakdownScenario}
          selectedBreakdownPeriod={selectedBreakdownPeriod}
          setSelectedBreakdownPeriod={setSelectedBreakdownPeriod}
          formatCurrency={formatCurrency}
          renderCostBreakdown={renderCostBreakdown}
          generateProfessionalPDF={generateProfessionalPDF}
          user={TylerState.user}
        />
      )
    }
    )
  }

  // Add CO₂ emissions section if enabled
  if (showResults && !inputsChanged && data.includeCO2Comparison) {
    resultsSection.push({
      id: 'co2-emissions',
      label: 'CO₂ Emissions',
      render: () => {
        const results = calculateResults(data);
        const comparisonResults = comparisonMode ? calculateResults(comparisonData) : null;

        return (
          <div>
            {comparisonMode && comparisonResults?.co2 ? (
              <div className="comparison-grid">
                <CO2SummaryCard
                  co2Results={results.co2!}
                  scenarioName={data.scenarioName || 'Car A'}
                  formatCurrency={formatCurrency}
                  comparisonMode={true}
                />
                <CO2SummaryCard
                  co2Results={comparisonResults.co2!}
                  scenarioName={comparisonData.scenarioName || 'Car B'}
                  formatCurrency={formatCurrency}
                  comparisonMode={true}
                />
              </div>
            ) : results.co2 ? (
              <CO2SummaryCard
                co2Results={results.co2}
                scenarioName={data.scenarioName || 'Car A'}
                formatCurrency={formatCurrency}
              />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                No CO₂ data available. Please ensure province/state is selected.
              </div>
            )}
          </div>
        );
      }
    });
  }

  // Combine input sections with results sections
  const allSections = [...sections, ...resultsSection];

  // Dynamic Next Button Component - uses allSections to navigate through both inputs and outputs
  const NextButton = () => {
    const currentIndex = allSections.findIndex(s => s.id === activeSection);
    const nextSection = currentIndex < allSections.length - 1 ? allSections[currentIndex + 1] : null;

    // If we're at the last input section and haven't calculated yet, show Calculate button
    if (!nextSection && !showResults) {
      return (
        <div className="dynamic-next-button">
          <button
            onClick={handleCalculate}
            className="run-simulation-btn primary"
          >
            Calculate Comparison
          </button>
        </div>
      );
    }

    // If there's a next section (either input or output), show Next button
    if (nextSection) {
      return (
        <div className="dynamic-next-button">
          <button
            onClick={() => handleSectionClick(nextSection.id)}
            className="run-simulation-btn primary"
          >
            Next: {nextSection.label} →
          </button>
        </div>
      );
    }

    return null;
  };

  // FAQ data based on country
  const canadaFAQs = [
    { question: "Is it cheaper to lease or buy a car in Canada?", answer: "It depends on your situation. Leasing typically has lower monthly payments but you don't build equity. Our calculator accounts for total cost of ownership including opportunity cost, taxes, depreciation, and insurance to give you a personalized recommendation based on your specific circumstances." },
    { question: "How does the lease vs buy calculator work?", answer: "Our calculator compares the total cost of leasing, financing, or buying a car outright over your ownership period. It includes purchase price, interest rates, down payments, insurance, maintenance, taxes, depreciation, and opportunity cost of invested capital to show you the true cost difference." },
    { question: "What's the difference between leasing and financing a car?", answer: "Leasing means you pay for the depreciation during your lease term and return the car at the end. Financing means you take out a loan to buy the car and own it once paid off. Leasing has lower monthly payments but no equity, while financing builds ownership but costs more monthly." },
    { question: "When should I lease a car instead of buying?", answer: "Leasing makes sense if you want lower monthly payments, prefer driving newer cars every few years, don't drive many kilometers annually, and want to avoid maintenance costs. It's especially beneficial for business use where you can deduct lease payments." },
    { question: "What is residual value in a car lease?", answer: "Residual value is the estimated worth of the car at the end of your lease term. It determines your monthly payment - the higher the residual value, the lower your payments. Our calculator helps you understand how residual value impacts your total leasing cost." },
    { question: "Does leasing or buying save more money long-term?", answer: "Buying typically saves more money if you keep the car for 5+ years, as you build equity and avoid continuous lease payments. However, leasing can be cheaper short-term with lower monthly costs. Our calculator shows you the exact difference for your specific scenario including opportunity costs." },
    { question: "How does HST/GST affect leasing vs buying in Canada?", answer: "When you buy a car in Canada, you pay HST/GST upfront on the full purchase price. When leasing, you only pay HST/GST on your monthly lease payments. This means leasing requires less tax upfront, but you pay tax throughout the lease term. Our calculator includes Canadian tax rates for accurate comparisons." },
    { question: "Can I claim a car lease as a business expense in Canada?", answer: "Yes, in Canada you can deduct lease payments as a business expense if you use the vehicle for business purposes. The CRA limits the monthly deductible amount to $800 plus HST/GST. Car purchases can be depreciated using CCA (Capital Cost Allowance). Our calculator helps you see the total cost to compare with tax benefits." },
    { question: "What's a good down payment for a car in Canada?", answer: "A good down payment is typically 20% of the purchase price. This helps you avoid being underwater on your loan and reduces your monthly payments. For a $30,000 car, that's $6,000 down. Our calculator shows how different down payment amounts affect your total cost including opportunity cost of that money." },
    { question: "Should I lease or buy a used car in Canada?", answer: "Leasing is rarely available for used cars - it's mainly for new vehicles. If you want a used car, buying is your best option. Buying used saves money on depreciation since someone else took the biggest hit. Our calculator can compare buying a used car vs leasing or buying new to show you the savings." }
  ];

  const usaFAQs = [
    { question: "Is it cheaper to lease or buy a car in the US?", answer: "It depends on your situation. Leasing typically has lower monthly payments but you don't build equity. Our calculator accounts for total cost of ownership including opportunity cost, sales tax, depreciation, and insurance to give you a personalized recommendation based on your specific circumstances." },
    { question: "How does the lease vs buy calculator work?", answer: "Our calculator compares the total cost of leasing, financing, or buying a car outright over your ownership period. It includes purchase price, interest rates, down payments, insurance, maintenance, sales tax, depreciation, and opportunity cost of invested capital to show you the true cost difference." },
    { question: "What's the difference between leasing and financing a car?", answer: "Leasing means you pay for the depreciation during your lease term and return the car at the end. Financing means you take out a loan to buy the car and own it once paid off. Leasing has lower monthly payments but no equity, while financing builds ownership but costs more monthly." },
    { question: "When should I lease a car instead of buying?", answer: "Leasing makes sense if you want lower monthly payments, prefer driving newer cars every few years, don't drive many miles annually, and want to avoid maintenance costs. It's especially beneficial for business use where you can deduct lease payments." },
    { question: "What is residual value in a car lease?", answer: "Residual value is the estimated worth of the car at the end of your lease term. It determines your monthly payment - the higher the residual value, the lower your payments. Our calculator helps you understand how residual value impacts your total leasing cost." },
    { question: "Does leasing or buying save more money long-term?", answer: "Buying typically saves more money if you keep the car for 5+ years, as you build equity and avoid continuous lease payments. However, leasing can be cheaper short-term with lower monthly costs. Our calculator shows you the exact difference for your specific scenario including opportunity costs." },
    { question: "How does sales tax affect leasing vs buying in the US?", answer: "Sales tax varies by state (0% in Oregon, Montana, NH, Delaware to over 10% in some states). When buying, you pay sales tax on the full price. When leasing, most states tax only monthly payments. Our calculator includes state-specific sales tax rates for accurate comparisons." },
    { question: "Can I claim a car lease as a business expense in the US?", answer: "Yes, in the US you can deduct lease payments as a business expense if you use the vehicle for business purposes. The IRS limits the deductible amount based on vehicle value. Car purchases can be depreciated using MACRS depreciation or Section 179 deductions. Our calculator helps you see the total cost to compare with tax benefits." },
    { question: "What's a good down payment for a car in the US?", answer: "A good down payment is typically 20% of the purchase price. This helps you avoid being underwater on your loan and reduces your monthly payments. For a $30,000 car, that's $6,000 down. Our calculator shows how different down payment amounts affect your total cost including opportunity cost of that money." },
    { question: "Which states have no sales tax on cars?", answer: "Five states have no sales tax: Alaska, Delaware, Montana, New Hampshire, and Oregon. This can save you thousands on a car purchase. However, some states charge other fees like registration or doc fees. Our calculator lets you enter 0% tax if you're in a no-tax state." }
  ];

  return (
    <div className="car-comparison-calculator">
      {/* SEO Structured Data - Country-specific FAQs */}
      <StructuredData
        type="faq"
        data={{ faqs: isUSA ? usaFAQs : canadaFAQs }}
      />
      <StructuredData
        type="breadcrumb"
        data={{
          items: [
            { name: "Home", url: "https://larryshelp.org" },
            { name: "Car Comparison Calculator", url: "https://larryshelp.org/car-comparison" }
          ]
        }}
      />

      {/* Header */}
      <div className="car-planner-header">
        <h1>Car Comparison Calculator</h1>
        <p className="subtitle">Compare the true cost of buying, financing, leasing, or not having a car</p>
        <Link to="/car-calculator-info" className="learn-more-btn">Learn More</Link>
      </div>

      {/* Mode Selection */}
      <div className="mode-selection">
        <div className="mode-buttons">
          <button 
            className={`mode-btn ${!comparisonMode ? 'active' : ''}`}
            onClick={() => handleComparisonToggle(false)}
            aria-pressed={!comparisonMode}
          >
            <span className="mode-title">Single Car</span>
            <span className="mode-desc">How much does a car truly cost to lease, finance, or buy?</span>
          </button>
          <button 
            className={`mode-btn ${comparisonMode ? 'active' : ''}`}
            onClick={() => handleComparisonToggle(true)}
            aria-pressed={comparisonMode}
          >
            <span className="mode-title">Comparison</span>
            <span className="mode-desc">Compare two cars to help you with your purchase decision.</span>
          </button>
        </div>
      </div>


      {/* Main Grid - Navigation + Content */}
      <div style={{width:"100%"}}>

        {/* Content Panel */}
        <div className="content-panel">
          {/* Mobile Navigation */}
          <div className="mobile-nav">
            {allSections.map(section => (
              <button
                key={section.id}
                className={`nav-button ${activeSection === section.id ? 'active' : ''} ${
                  resultsSection.some(r => r.id === section.id) ? 'results-nav' : ''
                }`}
                onClick={() => handleSectionClick(section.id)}
              >
                {section.label}
              </button>
            ))}
          </div>
          
          {/* Mobile Actions */}
          <div className="mobile-actions">
            <button 
              className="run-simulation-btn primary" 
              onClick={handleCalculate}
            >
              Calculate Comparison
            </button>
          </div>

          {/* Current Section */}
          {isInputSectionVisible && allSections.find(section => section.id === activeSection)?.render()}
          {/* Dynamic Next Button */}
          {isInputSectionVisible && <NextButton />}
        </div>
      </div>

      {inputsChanged && showResults && (
        <div className="update-notice">
          <p>⚠️ Inputs have changed. Click "Calculate Comparison" to update results.</p>
        </div>
      )}

      
      <DisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
        onAccept={async () => {
          setShowDisclaimerModal(false);

          // AUTO-SAVE: If logged in, save scenario(s) on calculate
          const currentUser = TylerState.user;
          if (currentUser) {
            const cap = getScenarioCap(currentUser.tier || 'free');

            // Save Car A (data) if it has a name
            const carAName = (data.scenarioName || '').trim();
            if (carAName) {
              const existingCarA = savedScenarios.find(s => s.name.toLowerCase() === carAName.toLowerCase());

              // Only save if: 1) updating existing OR 2) under cap for new scenario
              if (existingCarA || savedScenarios.length < cap) {
                try {
                  if (existingCarA) {
                    await carScenariosService.updateCarScenario(existingCarA.id, {
                      name: carAName,
                      description: saveDescription.trim() || undefined,
                      data: { ...data, scenarioName: carAName }
                    });
                  } else {
                    await carScenariosService.saveCarScenario({
                      name: carAName,
                      description: saveDescription.trim() || undefined,
                      data: { ...data, scenarioName: carAName }
                    });
                  }
                } catch (error) {
                  console.error('Auto-save Car A failed:', error);
                }
              }
            }

            // In comparison mode, also save Car B (comparisonData) if it has a name
            if (comparisonMode) {
              const carBName = (comparisonData.scenarioName || '').trim();
              if (carBName) {
                // Reload scenarios to get updated count after Car A save
                await loadSavedScenarios();
                const updatedScenarios = await carScenariosService.getCarScenariosArray();
                const existingCarB = updatedScenarios.find(s => s.name.toLowerCase() === carBName.toLowerCase());

                // Only save if: 1) updating existing OR 2) under cap for new scenario
                if (existingCarB || updatedScenarios.length < cap) {
                  try {
                    if (existingCarB) {
                      await carScenariosService.updateCarScenario(existingCarB.id, {
                        name: carBName,
                        description: saveDescription.trim() || undefined,
                        data: { ...comparisonData, scenarioName: carBName }
                      });
                    } else {
                      await carScenariosService.saveCarScenario({
                        name: carBName,
                        description: saveDescription.trim() || undefined,
                        data: { ...comparisonData, scenarioName: carBName }
                      });
                    }
                  } catch (error) {
                    console.error('Auto-save Car B failed:', error);
                  }
                }
              }
            }

            // Final reload to update UI
            await loadSavedScenarios();
          }

          // Supporters: never. Logged-in: 20% chance. Guests: always.
          const isSupporter = currentUser && currentUser.tier === 'supporter';
          const showAd = !isSupporter && (!currentUser || Math.random() < 0.2);
          if (showAd) {
            setShowAdModal(true);
          } else {
            // Batch state updates to prevent multiple re-renders
            React.startTransition(() => {
              setShowResults(true);
              setInputsChanged(false);
              // Auto-navigate to Results Summary after calculation
              setActiveSection('results-summary');
              setIsInputSectionVisible(true);
            });
          }
        }}
      />

      <GoogleAdInterstitial
        isOpen={showAdModal}
        onClose={() => {
          setShowAdModal(false);
          // Show results after ad closes
          React.startTransition(() => {
            setShowResults(true);
            setInputsChanged(false);
            setActiveSection('results-summary');
            setIsInputSectionVisible(true);
          });
        }}
        duration={15}
      />
      
      <GuidanceModal
        isOpen={showGuidanceModal}
        onClose={() => {
          setShowGuidanceModal(false);
          if (guidanceTimeoutRef.current) {
            clearTimeout(guidanceTimeoutRef.current);
            guidanceTimeoutRef.current = null;
          }
        }}
        onAccept={() => {
          setShowGuidanceModal(false);
          if (guidanceTimeoutRef.current) {
            clearTimeout(guidanceTimeoutRef.current);
            guidanceTimeoutRef.current = null;
          }
        }}
        content={guidanceContent}
        title={guidanceTitle}
      />

      {/* Save Scenario Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save Car Scenario</h3>
              <button className="modal-close" onClick={() => setShowSaveModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="scenario-name">Scenario Name *</label>
                <input
                  id="scenario-name"
                  type="text"
                  value={saveName}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setSaveName(newName);
                    // Update the scenarioName in the data object (single source of truth)
                    setData({ ...data, scenarioName: newName || 'Car A' });
                    // Keep TylerState in sync for backward compatibility
                    TylerState.carNameA = newName;
                    updateReact();
                  }}
                  placeholder="e.g., Range Rover Build"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="scenario-description">Description (optional)</label>
                <textarea
                  id="scenario-description"
                  value={saveDescription}
                  onChange={(e) => setSaveDescription(e.target.value)}
                  placeholder="Add notes about this scenario..."
                  className="form-textarea"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSaveModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveModalSubmit}
                disabled={!saveName.trim()}
              >
                Save Scenario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarComparisonCalculator; 